import type { ReactNode, Ref } from "react";

import { isRotationCode, maskRotation, rotationError } from "../centacRotation";
import { type Awaiting, awaitingAttr } from "../components/awaiting";
import { Motion, Presence } from "../components/presence";
import { Field, inputClass } from "../components/ui";
import { t } from "../i18n/i18n";
import { CENTAC_ROTATION_FIELD } from "./missionDataTarget";

/** Nom d'un dossier CENTAC, qui est aussi sa mission (demandes d'Alexis du 26/10/2026, registres A119 et A122) :
 *  « Rotation » suivi du numéro de la rotation jouée, « NN/AA » (masque de saisie : chiffres seuls, barre oblique posée
 *  d'elle-même), l'aide dessous (le numéro nomme le dossier et sa mission ; modèle de rotation), et l'erreur d'un
 *  numéro mal formé, qui apparaît en fondu une fois le champ quitté (`touched`) ou l'enregistrement demandé. Un seul
 *  champ, à la place du nom du dossier : aucun nom à saisir en plus. Exigé pour créer le dossier ; vide sur un dossier
 *  existant, il est demandé avant la rédaction de l'OPO N°1. `note` : ancien nom d'un dossier renommé, ou nom gardé
 *  jusqu'au numéro. Cible `dossier-centac-rotation` (`data-attention`), désignée par « Créer le dossier », « Générer
 *  l'ordre » et le déroulement de la rotation. */
export function CentacRotationField({
  value,
  onChange,
  touched,
  onTouched,
  conflict,
  note,
  inputRef,
  awaiting,
}: {
  value: string | null | undefined;
  onChange: (value: string | null) => void;
  /** Le champ a été quitté, ou l'enregistrement demandé : l'erreur s'affiche. */
  touched: boolean;
  onTouched: (touched: boolean) => void;
  /** Rappel d'un conflit avec le dossier enregistré (`ConflictHint`). */
  conflict?: ReactNode;
  /** Ligne discrète sous l'aide (ancien nom du dossier). */
  note?: ReactNode;
  inputRef?: Ref<HTMLInputElement>;
  /** Numéro attendu pour créer le dossier : surbrillance du champ (`components/awaiting.ts`). */
  awaiting?: Awaiting;
}) {
  const error = touched ? rotationError(value) : null;
  return (
    <div>
      <Field
        label={t("Nom du dossier et mission")}
        hint={t("Numéro de la rotation dans l'année / année, par exemple 08/26 : il nomme le dossier et sa mission. Modèle de rotation : OPO 1 prendre le contact, OPO 2 intercepter, OPO 3 attaquer, OPO 4 freiner (et recueillir).")}
      >
        <span className="flex items-center gap-2">
          <span className="text-ui font-medium text-ink">{t("Rotation")}</span>
          <span className="block w-24">
            <input
              ref={inputRef}
              data-attention={CENTAC_ROTATION_FIELD}
              {...awaitingAttr(awaiting)}
              className={inputClass}
              value={value ?? ""}
              onChange={(e) => {
                const masked = maskRotation(e.target.value);
                onChange(masked || null);
                if (isRotationCode(masked)) onTouched(false);
              }}
              onBlur={() => onTouched(true)}
              placeholder={t("NN/AA")}
              inputMode="numeric"
              autoComplete="off"
              spellCheck={false}
              maxLength={5}
              aria-invalid={error ? true : undefined}
            />
          </span>
        </span>
      </Field>
      <Presence show={Boolean(error)}>
        {error && (
          <Motion origin="fade" className="mt-1 block text-meta leading-snug text-danger" role="alert">
            {t(error)}
          </Motion>
        )}
      </Presence>
      {note && <p className="mt-1 text-meta leading-snug text-ink-muted">{note}</p>}
      {conflict}
    </div>
  );
}
